/**
 * Centralized assertion operator metadata for Add/Edit forms, payloads,
 * API normalization, and Expected Value column rendering.
 */

import type { RequestAssertion } from '../types/assertions';
import { normalizeAssertionOrFields } from './assertionOrLogic';

export type OperatorExpectedConfig = {
  requiresExpectedValue: boolean;
};

/** Exact operator keys used by the backend / ValidationOperator union. */
export const OPERATOR_CONFIG: Record<string, OperatorExpectedConfig> = {
  equals: { requiresExpectedValue: true },
  not_equals: { requiresExpectedValue: true },
  notequals: { requiresExpectedValue: true },
  contains: { requiresExpectedValue: true },
  not_contains: { requiresExpectedValue: true },
  starts_with: { requiresExpectedValue: true },
  ends_with: { requiresExpectedValue: true },
  gt: { requiresExpectedValue: true },
  gte: { requiresExpectedValue: true },
  lt: { requiresExpectedValue: true },
  lte: { requiresExpectedValue: true },
  // Backend regex uses `regexPattern`, not expectedValue — treat as no expected field.
  regex: { requiresExpectedValue: false },
  matches: { requiresExpectedValue: true },

  is_empty: { requiresExpectedValue: false },
  is_not_empty: { requiresExpectedValue: false },
  is_present: { requiresExpectedValue: false },
  is_string: { requiresExpectedValue: false },
  is_integer: { requiresExpectedValue: false },
  is_boolean: { requiresExpectedValue: false },
  is_array: { requiresExpectedValue: false },
  is_object: { requiresExpectedValue: false },
  is_email: { requiresExpectedValue: false },
  is_valid_email: { requiresExpectedValue: false },
  is_url: { requiresExpectedValue: false },
  is_date: { requiresExpectedValue: false },
  is_valid_token: { requiresExpectedValue: false },
  is_null: { requiresExpectedValue: false },
  is_not_null: { requiresExpectedValue: false },
};

export function normalizeOperator(operator: unknown): string {
  return String(operator ?? '')
    .trim()
    .toLowerCase();
}

export function operatorRequiresExpectedValue(operator: unknown): boolean {
  const op = normalizeOperator(operator);
  if (!op) return true;
  const config = OPERATOR_CONFIG[op];
  if (config) return config.requiresExpectedValue;
  // Unknown operators: show/require expected value to avoid silently dropping data.
  return true;
}

/**
 * Format expected values for table display.
 * Do not use truthy checks — 0, false, and "" must render as entered.
 */
export function formatExpectedValue(value: unknown): string {
  if (value === null || value === undefined) return 'NA';

  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  return String(value);
}

/**
 * Expected Value column: value-based operators show the exact value;
 * unary / type operators always show NA.
 */
export function formatAssertionExpectedDisplay(
  expectedValue: unknown,
  operator: unknown
): string {
  if (!operatorRequiresExpectedValue(operator)) {
    return 'NA';
  }
  return formatExpectedValue(expectedValue);
}

/** Resolve expected for save/update: null when operator does not require it. */
export function expectedValueForOperatorPayload(
  operator: unknown,
  expectedValue: unknown
): string | null {
  if (!operatorRequiresExpectedValue(operator)) {
    return null;
  }
  if (expectedValue === null || expectedValue === undefined) {
    return null;
  }
  if (typeof expectedValue === 'object') {
    try {
      return JSON.stringify(expectedValue);
    } catch {
      return String(expectedValue);
    }
  }
  return String(expectedValue);
}

/**
 * Normalize assertion payloads at the API boundary only.
 * Canonical field: expectedValue. Canonical operator: validationOperator.
 */
export function normalizeAssertion(raw: Record<string, unknown> | null | undefined): RequestAssertion {
  const source = (raw ?? {}) as Record<string, unknown>;
  const operator = normalizeOperator(
    source.validationOperator ?? source.operator ?? source.validation_operator
  );
  const expectedValue =
    source.expectedValue ??
    source.expected_value ??
    source.value ??
    null;
  const orFields = normalizeAssertionOrFields(source);

  return {
    ...(source as unknown as RequestAssertion),
    validationOperator: (operator || String(source.validationOperator ?? 'equals')) as RequestAssertion['validationOperator'],
    expectedValue: expectedValue as RequestAssertion['expectedValue'],
    logicalOperator: orFields.logicalOperator,
    ...(orFields.expectedValues ? { expectedValues: orFields.expectedValues } : {}),
    ...(orFields.orEvaluationDetails ? { orEvaluationDetails: orFields.orEvaluationDetails } : {}),
  };
}

export function normalizeAssertionList(
  items: unknown[] | null | undefined
): RequestAssertion[] {
  if (!Array.isArray(items)) return [];
  return items.map((item) => normalizeAssertion((item ?? {}) as Record<string, unknown>));
}
